import { requestLaravel } from "../../../../../../utils/api";
import { IProjectStatusAndReadiness, IImplementationReadiness, IStakeholderCommunicationReadiness, IEconomicAndCommercialViability, IFeasibilityOption, IProjectOptionRow, IProjectFeasibilityAndOptionsAnalysis, IDemandAndNeedsValidationEngine, ICbaCeaIntelligenceEngine, IRiskAndSensitivityAnalysisEngine } from "./_models";
import { ApiResponse } from "../../../../core/_models";
import { getProjectFieldSection, saveProjectFieldSection, withProjectFieldValues } from "../../../core/project-fields/_request";

const PREFEASIBILITY_STAGE_ID = 3;
// Stage key used by stage-neutral table APIs (project options) and section documents.
export const STAGE_THREE_KEY = "project_stage_3";

const ECONOMIC_AND_COMMERCIAL_VIABILITY_SECTION = "economic_and_commercial_viability";
export const CBA_CEA_INTELLIGENCE_SECTION = "cba_cea_intelligence";
const DEMAND_AND_NEEDS_VALIDATION_SECTION = "demand_and_needs_validation";
// Sections the step's supporting documents are stored against.
export const PROJECT_STATUS_AND_READINESS_SECTION = "project_status_and_readiness";
export const IMPLEMENTATION_READINESS_SECTION = "implementation_readiness";
export const STAKEHOLDER_AND_COMMUNICATION_READINESS_SECTION = "stakeholder_and_communication_readiness";
export const RISK_AND_QUALITY_GATE_SECTION = "risk_and_quality_gate";
// Sub-section (tab) of risk_and_sensitivity_analysis; the Risk Analysis tab saves on the risk-management API.
const SENSITIVITY_ANALYSIS_SECTION = "rsa_sensitivity_analysis";

function readStageSection<T>(projectId: string | number, sectionKey: string, signal?: AbortSignal) {
    return getProjectFieldSection(projectId, PREFEASIBILITY_STAGE_ID, sectionKey, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

function writeStageSection<T>(projectId: string | number, sectionKey: string, payload: object, signal?: AbortSignal) {
    return saveProjectFieldSection(projectId, PREFEASIBILITY_STAGE_ID, sectionKey, payload, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

// A section's saved values as form values: fields not saved yet come back as null and keep their defaults.
export function updateStakeholderCommunicationReadiness(updateData: IStakeholderCommunicationReadiness & { project_id: number }) {
    return requestLaravel<ApiResponse<IStakeholderCommunicationReadiness>>(
        "/project-stage-three/stakeholder-communication-readiness/update",
        updateData
    );
}

export function getStakeholderCommunicationReadiness(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IStakeholderCommunicationReadiness>>(
        "/project-stage-three/stakeholder-communication-readiness/get",
        { project_id },
        { signal }
    );
}

export function getProjectStatusAndReadiness(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectStatusAndReadiness>>(
        "/project-stage-three/project-status-readiness/get",
        { project_id },
        { signal }
    );
}

interface UpdateProjectStatusAndReadinessPayload extends IProjectStatusAndReadiness {
    project_id: number;
}

export function updateProjectStatusAndReadiness(updateData: UpdateProjectStatusAndReadinessPayload) {
    return requestLaravel<ApiResponse<IProjectStatusAndReadiness>>(
        "/project-stage-three/project-status-readiness/update",
        updateData
    );
}

export function updateImplementationReadiness(updateData: IImplementationReadiness & { project_id: number }) {
    return requestLaravel<ApiResponse<IImplementationReadiness>>(
        "/project-stage-three/implementation-readiness/update",
        updateData
    );
}

export function getImplementationReadiness(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IImplementationReadiness>>(
        "/project-stage-three/implementation-readiness/get",
        { project_id },
        { signal }
    );
}

export function saveProjectStageThree(payload: { project_id: number; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<void>>(
        "/project-stage-three/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

export function getFeasibilityReadinessPercentage(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ readiness_percentage: number; average_risk_score: number }>>(
        "/project-stage-three/feasibility-readiness/get",
        payload,
        { signal }
    );
}


/* =========================
   ECONOMIC & COMMERCIAL VIABILITY
========================= */

// One request for all seven sub-sections (tabs); a save sends only the fields it has.
export function getEconomicAndCommercialViability(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<IEconomicAndCommercialViability>>(project_id, ECONOMIC_AND_COMMERCIAL_VIABILITY_SECTION, signal);
}

export function updateEconomicAndCommercialViability(updateData: IEconomicAndCommercialViability & { project_id: number }) {
    return writeStageSection<IEconomicAndCommercialViability>(updateData.project_id, ECONOMIC_AND_COMMERCIAL_VIABILITY_SECTION, updateData);
}


/* =========================
   OPTIONS IDENTIFICATION & SCREENING
========================= */

export function getProjectOptions(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectOptionRow[]>>(
        "/project/options/list",
        { project_id, stage_id: STAGE_THREE_KEY },
        { signal }
    );
}

export function saveProjectOption(payload: IFeasibilityOption & { project_id: number }, signal?: AbortSignal) {
    const { row_id, project_id, option_name, description, capital_cost, operating_cost, delivery_time, expected_benefits, strategic_alignment, complexity, risk_rating } = payload;
    return requestLaravel<ApiResponse<IProjectOptionRow>>(
        "/project/options/save",
        {
            project_id,
            stage_id: STAGE_THREE_KEY,
            ...(row_id ? { row_id } : {}),
            option_name,
            description,
            capital_cost,
            operating_cost,
            delivery_time,
            expected_benefits,
            strategic_alignment,
            complexity,
            risk_rating,
        },
        { signal }
    );
}

export function deleteProjectOption(payload: { project_id: number; row_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ row_id: number }>>(
        "/project/options/delete",
        { ...payload, stage_id: STAGE_THREE_KEY },
        { signal }
    );
}

// Builds the justification map ({ "<row_id>": "<html>" }) from the listed option rows.
export function toJustificationMap(rows: IProjectOptionRow[]): Record<string, string> {
    return rows.reduce<Record<string, string>>((acc, row) => {
        if (row.row_id && row.option_analysis_description) {
            acc[String(row.row_id)] = row.option_analysis_description;
        }
        return acc;
    }, {});
}

// Saves the option justifications; the API responds with the full option list, which is
// mapped back to the step's form shape.
export async function saveOptionJustifications(payload: IProjectFeasibilityAndOptionsAnalysis & { project_id: number }, signal?: AbortSignal): Promise<ApiResponse<IProjectFeasibilityAndOptionsAnalysis>> {
    const response = await requestLaravel<ApiResponse<IProjectOptionRow[]>>(
        "/project/options/justifications/save",
        {
            project_id: payload.project_id,
            stage_id: STAGE_THREE_KEY,
            justification_of_option: payload.justification_of_option,
        },
        { signal }
    );

    if (response.status !== "ok" || !Array.isArray(response.data)) {
        return { ...response, data: payload };
    }

    return { ...response, data: { justification_of_option: toJustificationMap(response.data) } };
}

/* =========================
   DEMAND & NEEDS VALIDATION
========================= */

// Also returns current_capacity, the Stage 2 Base Case value shown read-only in this section.
export function getDemandAndNeedsValidation(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<IDemandAndNeedsValidationEngine> & { current_capacity?: number | null }>(project_id, DEMAND_AND_NEEDS_VALIDATION_SECTION, signal);
}

// Sends only the section's own fields: current_capacity is read-only here and the API rejects it.
export function updateDemandAndNeedsValidation(updateData: IDemandAndNeedsValidationEngine & { project_id: number }) {
    const { current_demand_units, future_demand_horizon_year, population_served, growth_rate, peak_capacity_required, demand_confidence_level } = updateData;
    return writeStageSection<IDemandAndNeedsValidationEngine>(
        updateData.project_id,
        DEMAND_AND_NEEDS_VALIDATION_SECTION,
        { current_demand_units, future_demand_horizon_year, population_served, growth_rate, peak_capacity_required, demand_confidence_level },
    );
}

/* =========================
   CBA / CEA INTELLIGENCE
========================= */

export function getCbaCeaIntelligence(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<ICbaCeaIntelligenceEngine>>(project_id, CBA_CEA_INTELLIGENCE_SECTION, signal);
}

export function updateCbaCeaIntelligence(updateData: ICbaCeaIntelligenceEngine & { project_id: number }) {
    return writeStageSection<ICbaCeaIntelligenceEngine>(updateData.project_id, CBA_CEA_INTELLIGENCE_SECTION, updateData);
}

/* =========================
   RISK & SENSITIVITY ANALYSIS
========================= */

export function getSensitivityAnalysis(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<IRiskAndSensitivityAnalysisEngine>>(project_id, SENSITIVITY_ANALYSIS_SECTION, signal);
}

export function updateSensitivityAnalysis(updateData: IRiskAndSensitivityAnalysisEngine & { project_id: number }) {
    return writeStageSection<IRiskAndSensitivityAnalysisEngine>(updateData.project_id, SENSITIVITY_ANALYSIS_SECTION, updateData);
}
